import { useState, useEffect, useRef, useMemo } from "react"; 
import {
  TextField,
  Box,
  Divider,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  Grid,
  Paper,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  handleError,
  validateNonEmpty,
  resolveDateFormattingLocale,
  bcp47ToDayjsLocale,
  getDayjsDateTimeFormatForLocale,
  resolveHour12Preference,
} from "../../utils/lib";
import axios from "axios";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import 'dayjs/locale/en-gb';
import EditPage from "./EditPage";
import { useConfig } from '../../providers/ConfigProvider';
import { useTranslation } from 'react-i18next';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import PetsIcon from '@mui/icons-material/Pets';
import { uploadFiles, uploadSingleFile, markFileAsDeleted, prepareFilesForBackend, getProgressMessage } from '../../utils/fileUploadHelper';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import InlinePhotoCapture from "../../components/InlinePhotoCapture";
import MarkdownEditor from "../../components/MarkdownEditor";

const EditClinic = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { updateTitle, toggleFab } = useAppBar();
  const { config, isLoadingConfig } = useConfig();
  const { t } = useTranslation();
  const [ isLoading, setIsLoading ] = useState(false);

  const dateFormattingLocale = useMemo(() => resolveDateFormattingLocale(config), [config]);
  const useHour12 = useMemo(
    () => resolveHour12Preference(config, dateFormattingLocale),
    [config, dateFormattingLocale]
  );
  const dateTimePickerFormat = useMemo(
    () => getDayjsDateTimeFormatForLocale(dateFormattingLocale, useHour12),
    [dateFormattingLocale, useHour12]
  );
  const dayjsPickerLocale = useMemo(
    () => bcp47ToDayjsLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );

  const isMetricUnits = useMemo(
    () => (config?.vet?.unit_system ?? 'METRIC') === 'METRIC',
    [config?.vet?.unit_system]
  );

  const { updateClinic, pet } = location.state || {};

  const [clinic, setClinic] = useState(() => {
    if (updateClinic) {
      let dateValue = updateClinic.date;
      // Always reconstruct as Dayjs, even if it looks like a Dayjs object
      dateValue = dayjs(
        typeof dateValue === 'object' && dateValue !== null && dateValue.$d
          ? dateValue.$d
          : dateValue
      );
      return {
        ...updateClinic,
        date: dateValue,
        pet: pet
      };
    }
    return {
      date: dayjs(),
      temp: "",
      weight: "",
      description: "",
      pet: pet,
    };
  });

  const [errors, setErrors] = useState({});
  const refs = {
    date: useRef(null),
    temp: useRef(null),
    weight: useRef(null),
    description: useRef(null),
  };

  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState({ open: false, text: '', severity: 'info' });
  const fileInputRef = useRef(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [showInlineCamera, setShowInlineCamera] = useState(false);
  const { setNavigation, from, state: navState, resetNavigation } = useNavigationHelperStore();

  useEffect(() => {
    setIsLoading(true);

    updateTitle(
      clinic?.pet?.thumb_url || '',
      clinic?.pet?.name || '',
      clinic.id ? t('edit.editClinic') : t('edit.newClinic'),
      false,
      PetsIcon
    );

    if (clinic.resources) {
      setAttachedFiles(clinic.resources);
    }

    toggleFab(false);
    setIsLoading(false);

    // Set focus on first field (description)
    if (refs.description.current) {
      refs.description.current.focus();
    }
  }, []);

  // Restaurar borrador si volvemos desde la cámara
  useEffect(() => {
    if (from === '/main/edit-clinic' && navState) {
      if (navState.clinic) {
        // Aseguramos que la fecha siga siendo un objeto Dayjs
        setClinic((prev) => ({
          ...prev,
          ...navState.clinic,
          date: dayjs(navState.clinic.date)
        }));
      }
      if (navState.attachedFiles) {
        setAttachedFiles(navState.attachedFiles);
      }
    }
  }, [from, navState]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setClinic({ ...clinic, [name]: value });
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const fieldOrder = ['date', 'temp', 'weight', 'description'];
      const currentIndex = fieldOrder.indexOf(fieldName);
      
      if (currentIndex >= 0 && currentIndex < fieldOrder.length - 1) {
        // Move to the next field
        const nextField = fieldOrder[currentIndex + 1];
        if (refs[nextField] && refs[nextField].current) {
          refs[nextField].current.focus();
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !validateNonEmpty("description", clinic.description, setErrors, refs.description)
    )
      return;

      save();
  };

  const save = async () => {
    setIsLoading(true);

    try {
      let response;
      const clinicData = {
        ...clinic,
        date: dayjs(clinic.date).format('YYYY-MM-DD HH:mm:ss'),
        resources: prepareFilesForBackend(attachedFiles)
      };

      //console.log(clinicData);

      if (clinicData.id) {
        response = await axios.put(
          `${import.meta.env.VITE_API_URL}pets_clinic?id=${clinicData.id}`,
          clinicData,
          { withCredentials: true }
        );
      } else {
        response = await axios.post(
          `${import.meta.env.VITE_API_URL}pets_clinic`,
        clinicData,
        { withCredentials: true }
      );
      }

      //navigate(-1);
      navigate('/main/view-pet', { state: { id: clinic?.pet?.id, updateLastView: false, ...navState?.state } }, { replace: true });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
      resetNavigation();
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setUploading(true);
    setUploadMessage({
      open: true,
      text: t('common.uploadingFiles'),
      severity: 'info'
    });

    try {
      const result = await uploadFiles({
        files,
        tableName: 'pets_clinic',
        type: 'PRIVATE',
        onProgress: (progress, file, index, total) => {
          setUploadMessage({
            open: true,
            text: getProgressMessage(index, total, progress),
            severity: 'info'
          });
        },
        onFileComplete: (uploadedFile) => {
          setAttachedFiles(prev => [...prev, uploadedFile]);
        },
        onError: (error, file) => {
          setUploadMessage({
            open: true,
            text: error,
            severity: 'error'
          });
        }
      });

      if (result.success) {
        setUploadMessage({
          open: true,
          text: t('common.filesUploadedSuccess'),
          severity: 'success'
        });
      } else if (result.successfulUploads > 0) {
        setUploadMessage({
          open: true,
          text: `${result.successfulUploads} / ${result.totalFiles} ${t('common.filesUploadedSuccess')}`,
          severity: 'warning'
        });
      }
    } catch (error) {
      setUploadMessage({
        open: true,
        text: t('common.errorUploadingImages'),
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (index) => {
    const fileToDelete = attachedFiles[index];
    const updatedFiles = markFileAsDeleted(attachedFiles, fileToDelete.unique_id);
    setAttachedFiles(updatedFiles);
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleCameraClick = () => {
    setShowInlineCamera(true);
  };

  const handleInlinePhotoConfirm = async (file, previewUrl) => {
    setUploading(true);
    setUploadMessage({ open: true, text: t('common.uploadingFiles'), severity: 'info' });
    
    try {
      const uploaded = await uploadSingleFile({
        file: file,
        tableName: 'pets_clinic',
        type: 'PRIVATE',
        onProgress: (progress) => {
          setUploadMessage({ open: true, text: getProgressMessage(0, 1, progress), severity: 'info' });
        }
      });
      setAttachedFiles((prev) => [...prev, uploaded]);
      setUploadMessage({ open: true, text: t('common.photoUploadedSuccess'), severity: 'success' });
    } catch (error) {
      setUploadMessage({ open: true, text: error.message || t('common.errorUploadingImages'), severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <EditPage 
      onSubmit={handleSubmit}
      isLoading={isLoading || isLoadingConfig}
    >
      <Box sx={{ width: "100%", marginTop: 1 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <DateTimePicker
            label={t('common.dateTime')}
            // value={dateValue}
            value={clinic.date}
            onChange={(newValue) => setClinic({ ...clinic, date: newValue })}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                margin="normal"
                name="date"
                error={!!errors.date}
                helperText={errors.date}
                inputRef={refs.date}
                onKeyDown={(e) => handleKeyDown(e, 'date')}
              />
            )}
            slotProps={{ textField: { fullWidth: true, inputRef: refs.date, onKeyDown: (e) => handleKeyDown(e, 'date') } }}
            format={dateTimePickerFormat}
            ampm={useHour12}
          />
        </LocalizationProvider>
      </Box>

      <Box display="flex" gap={2}>
        <TextField
          fullWidth
          margin="normal"
          label={t('edit.temperature', { unit: isMetricUnits ? '°C' : '°F' })}
          name="temp"
          value={clinic.temp}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            setClinic({ ...clinic, temp: value });
          }}
          inputProps={{
            inputMode: 'decimal',
            pattern: '[0-9]*[.,]?[0-9]*'
          }}
          inputRef={refs.temp}
          onKeyDown={(e) => handleKeyDown(e, 'temp')}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t('edit.weightLabel', { unit: isMetricUnits ? 'kg' : 'lb' })}
          name="weight"
          value={clinic.weight}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            setClinic({ ...clinic, weight: value });
          }}
          inputProps={{
            inputMode: 'decimal',
            pattern: '[0-9]*[.,]?[0-9]*'
          }}
          inputRef={refs.weight}
          onKeyDown={(e) => handleKeyDown(e, 'weight')}
        />
      </Box>

      <MarkdownEditor
        label={t('edit.descriptionStar')}
        name="description"
        value={clinic.description}
        onChange={handleChange}
        inputRef={refs.description}
        error={!!errors.description}
        helperText={errors.description}
      />

      <Divider sx={{ my: 2 }} />

      <InlinePhotoCapture 
        open={showInlineCamera}
        onPhotoConfirm={handleInlinePhotoConfirm}
        onClose={() => setShowInlineCamera(false)}
      />

      <Box display="flex" gap={4} justifyContent="center">
        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            style={{ display: 'none' }}
          />
          <IconButton onClick={handleUploadClick} disabled={uploading}>
            <AttachFileIcon />
          </IconButton>
          <Typography variant="caption">{t('common.file')}</Typography>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
          <IconButton onClick={handleCameraClick} disabled={uploading}>
            <PhotoCameraIcon />
          </IconButton>
          <Typography variant="caption">{t('common.photo')}</Typography>
        </Box>
      </Box>

      {/*hasAttachedFiles*/}
      {attachedFiles.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {attachedFiles.filter(file => file.deleted !== 1).map((file, filteredIndex) => {
              // Encontrar el índice original del archivo en el array completo
              const originalIndex = attachedFiles.findIndex(f => f.unique_id === file.unique_id);
              
              return (
                <Grid item xs={4} key={file.unique_id}>
                  <Paper 
                    sx={{ 
                      position: 'relative',
                      height: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    {file.type.startsWith('image/') ? (
                      <img 
                        src={file.url} 
                        alt={file.name}
                        style={{ 
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <AttachFileIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    )}
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)'
                      }}
                      onClick={() => handleDeleteFile(originalIndex)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      textAlign: 'center',
                      mt: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {file.name}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      <Snackbar
        open={uploadMessage.open} 
        autoHideDuration={6000}
        onClose={() => setUploadMessage(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setUploadMessage(prev => ({ ...prev, open: false }))} 
          severity={uploadMessage.severity}
          sx={{ width: '100%' }}
        >
          {uploadMessage.text}
        </Alert>
      </Snackbar>

    </EditPage>
  );
};

export default EditClinic;